/**
 * Карточка сайта на каркасе сущности — окно списка «Карточки Сайт».
 *
 * Те же шапка, колонка, цепочка, «Что дальше» с проверками и единые вкладки,
 * что у ВБ, ОЗОН и Маркета (`McardWindow`). Показывается ровно то, что уедет
 * на витрину: замороженная запись собирается тем же кодом, что и выгрузка.
 * Второго сборщика для предпросмотра нет — иначе экран показывал бы одно, а
 * плагин получал другое.
 *
 * Правка из окна недоступна: тексты и характеристики сайта собираются из ПИМ и
 * SEO при выгрузке, а журнал правок карточки Сайта не обслуживает. Фото
 * правятся менеджером медиа товара (вкладка «Медиа»), выгрузка — кнопкой
 * «Выгрузить на сайт» в полосе «Что дальше».
 */

import { useMemo, useState } from 'react'
import { CloudUpload, ExternalLink, Wrench } from 'lucide-react'

import { Badge, Modal } from '../../components/ui'
import type { BadgeVariant } from '../../components/ui/Badge'
import { McardWindow } from '../marketplace-catalog/components/mp-card/McardWindow'
import { Notice, Panel, ReadonlyValue } from '../marketplace-catalog/components/mp-card/McardParts'
import {
  formatRub,
  type McardCharacteristic,
  type McardIssue,
  type McardModel,
  type McardShowcase,
} from '../marketplace-catalog/components/mp-card/model'
import { STATUS_LABELS, dateTime, reasonLabel, seoSourceLabel } from './cardColumns'
import type { SiteProductCard, SiteProductStatus } from './types'
import { ProductMediaManager } from '../products-v2/components/ProductMediaManager'

/** Подписи решений журнала. Те же, что на вкладке «Настройки обмена». */
const DECISION_LABELS: Record<string, string> = {
  created: 'Создана',
  updated: 'Обновлена',
  unchanged: 'Без изменений',
  skipped: 'Пропущена',
  gap: 'Уехала неполной',
  failed: 'Ошибка',
}

const MODE_LABELS: Record<string, string> = {
  full: 'Полная сверка',
  incremental: 'Инкремент',
  price: 'Цены',
  selection: 'Точечная выгрузка',
}

const decisionBadge = (decision: string) => {
  const value = decision.toLowerCase()
  const label = DECISION_LABELS[value] ?? decision ?? '—'
  if (value === 'failed') return <Badge variant="red">{label}</Badge>
  if (value === 'skipped' || value === 'gap') return <Badge variant="yellow">{label}</Badge>
  if (value === 'unchanged') return <Badge variant="gray">{label}</Badge>
  return <Badge variant="green">{label}</Badge>
}

const STATUS_VARIANTS: Record<SiteProductStatus, BadgeVariant> = {
  draft: 'gray',
  creating: 'gray',
  created: 'green',
  price_set: 'green',
  stock_set: 'green',
  selling: 'green',
  error: 'red',
  not_ready: 'yellow',
}

const ON_SITE = new Set<SiteProductStatus>(['created', 'price_set', 'stock_set', 'selling'])

const READ_ONLY_REASON =
  'Тексты и характеристики сайта собираются из ПИМ и SEO при выгрузке: правьте их там, затем нажмите «Выгрузить на сайт». Фото правятся на вкладке «Медиа».'

interface SiteCardModalProps {
  /** Артикул открытой карточки; null — модалка закрыта */
  article: string | null
  card: SiteProductCard | null
  loading: boolean
  /** Ключи подгруженных строк — для стрелок «N из M» */
  keys: string[]
  onNavigate: (article: string) => void
  onClose: () => void
  /** Точечная выгрузка одной карточки; недоступна, пока идёт прогон */
  onExchange?: (article: string) => void
  exchangeDisabledReason?: string | null
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
}

type RecordLike = Record<string, any>

const asText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ')
  if (typeof value === 'object') {
    const record = value as RecordLike
    if ('value' in record) return `${asText(record.value)}${record.unit ? ` ${record.unit}` : ''}`.trim()
    return ''
  }
  return String(value)
}

const ATTRIBUTE_LABELS: Array<[string, string]> = [
  ['recipients', 'Кому'],
  ['personas', 'Персоны'],
  ['gender', 'Пол'],
  ['age', 'Возраст'],
  ['occasion', 'Повод'],
  ['brand', 'Бренд'],
  ['nameOnImage', 'Имя на картинке'],
  ['ageOnImage', 'Возраст на картинке'],
]

/** Атрибуты записи сайта — только чтение: справочника площадки у сайта нет. */
export const siteCharacteristics = (record: RecordLike | null): McardCharacteristic[] => {
  const attributes = (record?.attributes ?? {}) as RecordLike
  return ATTRIBUTE_LABELS.map(([key, name]) => ({
    key,
    targetId: null,
    name,
    required: false,
    value: asText(attributes[key]),
  }))
}

export const buildSiteModel = (card: SiteProductCard, article: string): McardModel => {
  const product = card.product
  const record = (card.record ?? null) as RecordLike | null
  const seo = (record?.seo ?? {}) as RecordLike
  const gallery: string[] = [
    ...(record?.media?.main?.url ? [String(record.media.main.url)] : []),
    ...(Array.isArray(record?.media?.gallery) ? record!.media.gallery.map((item: any) => String(item?.url ?? '')) : []),
  ].filter(Boolean)
  const photos = gallery.length ? Array.from(new Set(gallery)) : product.visualUrl ? [product.visualUrl] : []
  const issues: McardIssue[] = [
    ...card.skipped.map((item) => ({ level: 'error' as const, title: 'Карточка на сайт не уедет', text: reasonLabel(item.reason) })),
    ...card.gaps.map((item) => ({ level: 'warning' as const, title: 'Запись уедет неполной', text: reasonLabel(item.reason) })),
  ]
  const onSite = ON_SITE.has(product.status)
  let showcase: McardShowcase
  if (onSite) showcase = { tone: 'ok', value: 'на сайте' }
  else if (product.status === 'error') {
    showcase = {
      tone: 'bad',
      value: 'ошибка обмена',
      problem: { title: 'Последний обмен с сайтом закончился ошибкой', hint: product.reason ? reasonLabel(product.reason) : 'Подробности — во вкладке «История».' },
    }
  } else {
    showcase = {
      tone: 'warn',
      value: product.status === 'not_ready' ? 'не готова' : 'не на сайте',
      problem: {
        title: product.status === 'not_ready' ? 'Карточка не готова к выгрузке на сайт' : 'Карточка ещё не выгружена на сайт',
        hint: product.reason ? reasonLabel(product.reason) : 'Нажмите «Выгрузить на сайт», когда всё готово.',
      },
    }
  }
  const title = product.siteTitle || asText(record?.title) || product.pimTitle || ''
  const stockQuantity = record?.stockQuantity
  return {
    platform: 'site',
    article,
    title,
    description: asText(seo.description) || asText(seo.text),
    brand: asText(record?.attributes?.brand) || null,
    statusLabel: STATUS_LABELS[product.status] ?? product.status,
    statusVariant: STATUS_VARIANTS[product.status] ?? 'gray',
    showcase: { ...showcase, onShowcase: onSite },
    photos,
    price: { shown: product.price, currency: 'RUB' },
    stock: stockQuantity !== null && stockQuantity !== undefined && Number.isFinite(Number(stockQuantity)) ? Number(stockQuantity) : null,
    stockWhy: record?.stockStatus ? `наличие: ${record.stockStatus === 'instock' ? 'в наличии' : 'нет в наличии'}, количество не задано` : 'запись не собрана',
    issues,
    characteristics: siteCharacteristics(record),
    specsKnown: true,
    lastSyncAt: product.lastRunAt,
    lastSyncLabel: 'Последний обмен',
    platformUrl: null,
    idMeta: [
      { label: 'ID на сайте', value: product.externalId || (product.postId != null ? String(product.postId) : null), mono: true, copy: true },
      { label: 'Носитель', value: product.carrier },
      { label: 'Категория', value: product.category },
    ],
    barcode: null,
    cabinetLabel: 'Сайт',
    designCode: product.designCode,
    pimArticle: product.article,
    themeNumber: product.theme,
    dimensionsText: null,
    categoryName: product.category,
    pimCheck: {
      tone: 'none',
      title: 'запись сайта собирается из ПИМ при выгрузке, отдельной копии для сравнения нет',
    },
    characteristicsCheck: {
      tone: 'none',
      title: 'у сайта нет справочника характеристик и обязательных полей: атрибуты записи только для чтения',
    },
  }
}

/** Читаемый вид записи: ключевые поля, а не JSON. */
const RecordView = ({ record }: { record: RecordLike | null }) => {
  if (!record) {
    return <Notice>Запись не собрана — на сайт уедет нечему.</Notice>
  }
  const seo = (record.seo ?? {}) as RecordLike
  const carrier = (record.carrier ?? {}) as RecordLike
  const gallery = Array.isArray(record.media?.gallery) ? record.media.gallery.length : 0
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <ReadonlyValue label="Артикул" value={asText(record.article)} mono />
      <ReadonlyValue label="Наименование" value={asText(record.title)} />
      <ReadonlyValue label="Раздел" value={asText(record.section)} />
      <ReadonlyValue label="Категория" value={asText(record.category)} />
      <ReadonlyValue label="Тематика" value={asText(record.theme)} />
      <ReadonlyValue label="Носитель" value={[asText(carrier.name), asText(carrier.prefix)].filter(Boolean).join(' · ')} />
      <ReadonlyValue label="Цена" value={record.price != null ? formatRub(record.price) : null} why="цена не задана" />
      <ReadonlyValue label="Наличие" value={record.stockStatus === 'instock' ? 'В наличии' : record.stockStatus === 'outofstock' ? 'Нет в наличии' : null} />
      <ReadonlyValue label="Источник текста" value={asText(seo.source)} />
      <ReadonlyValue label="SEO-заголовок" value={asText(seo.title)} />
      <ReadonlyValue label="SEO-описание" value={asText(seo.description)} />
      <ReadonlyValue label="Текст" value={asText(seo.text)} />
      <ReadonlyValue label="Картинок в галерее" value={`${gallery}`} />
      <ReadonlyValue label="Обновлено" value={record.updatedAt ? dateTime(String(record.updatedAt)) : null} />
    </div>
  )
}

export function SiteCardModal({
  article,
  card,
  loading,
  keys,
  onNavigate,
  onClose,
  onExchange,
  exchangeDisabledReason,
  canGoBack,
  onBack,
}: SiteCardModalProps) {
  const [technicalOpen, setTechnicalOpen] = useState(false)
  const model = useMemo(() => (card && article ? buildSiteModel(card, article) : null), [card, article])
  const product = card?.product ?? null

  if (!article) return null

  const priceContent = (
    <div className="space-y-4">
      <Panel title="Цена сайта">
        <div className="grid gap-4 sm:grid-cols-3">
          <ReadonlyValue label="Цена" value={product?.price != null ? formatRub(product.price) : null} why="цена не задана: карточка уедет без цены" />
          <ReadonlyValue label="Остаток" value={model?.stock != null ? `${model.stock} шт.` : null} why={model?.stockWhy} />
        </div>
      </Panel>
    </div>
  )

  const overviewExtra = (
    <Panel title="Состояние на сайте">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <ReadonlyValue label="Статус" value={product ? STATUS_LABELS[product.status] ?? product.status : null} />
        <ReadonlyValue label="ID на витрине" value={product?.externalId || null} mono why="карточка ещё не создана на сайте" />
        <ReadonlyValue label="Тип макета" value={product?.designType || null} />
        <ReadonlyValue label="Текст" value={product ? seoSourceLabel(product.seoSource) : null} />
        <ReadonlyValue label="Причина" value={product?.reason ? reasonLabel(product.reason) : null} why="причин нет" />
        <ReadonlyValue label="Обновлён" value={product?.updatedAt ? dateTime(product.updatedAt) : null} />
      </div>
    </Panel>
  )

  const textFooter = (
    <Panel title="Что уедет на сайт">
      <RecordView record={(card?.record ?? null) as RecordLike | null} />
    </Panel>
  )

  const historyExtra = (
    <Panel title="История обмена">
      {!card || card.history.length === 0 ? (
        <Notice>Эта карточка ещё не участвовала ни в одном прогоне.</Notice>
      ) : (
        <div className="divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-200 dark:divide-zinc-800 dark:border-zinc-800">
          {card.history.map((item, index) => (
            <div key={`${item.runId}-${index}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 text-sm">
              {decisionBadge(item.decision)}
              <span className="text-gray-700 dark:text-zinc-300">{MODE_LABELS[item.mode] ?? item.mode ?? '—'}</span>
              {item.dryRun && <span className="text-2xs text-gray-500 dark:text-zinc-400">сухой прогон</span>}
              {item.postId !== null && (
                <span className="inline-flex items-center gap-1 font-mono text-xs text-indigo-700 dark:text-indigo-300">
                  <ExternalLink className="h-3 w-3" />
                  {item.postId}
                </span>
              )}
              {(item.reason || item.error) && (
                <span className="min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-zinc-400">
                  {item.error || reasonLabel(item.reason)}
                </span>
              )}
              <span className="ml-auto whitespace-nowrap tabular-nums text-xs text-gray-500 dark:text-zinc-400">
                {dateTime(item.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  )

  return (
    <>
      <McardWindow
        platform="site"
        sellerId={null}
        windowKey={article}
        model={model}
        loading={loading || !card}
        keys={keys}
        activeKey={article}
        onNavigate={onNavigate}
        onClose={onClose}
        canGoBack={canGoBack}
        onBack={onBack}
        refreshing={false}
        onReloadDetail={() => undefined}
        linkParam="article"
        mediaEditable={false}
        renderMedia={() => <ProductMediaManager key={article} article={article} />}
        priceContent={priceContent}
        overviewExtra={overviewExtra}
        textFooter={textFooter}
        historyExtra={historyExtra}
        readOnly={{ reason: READ_ONLY_REASON }}
        extraActionsFirst
        extraActions={
          onExchange
            ? [
                {
                  key: 'exchange',
                  label: 'Выгрузить на сайт',
                  icon: <CloudUpload className="h-4 w-4" />,
                  disabledReason: exchangeDisabledReason ?? undefined,
                  onClick: () => onExchange(article),
                },
              ]
            : []
        }
        extraMenu={[
          {
            key: 'technical',
            label: 'Технические данные',
            icon: <Wrench className="h-4 w-4" />,
            disabledReason: card?.record ? undefined : 'Запись не собрана',
            onClick: () => setTechnicalOpen(true),
          },
        ]}
        emptyImageText="На сайте нет фото"
      />

      <Modal
        isOpen={technicalOpen}
        onClose={() => setTechnicalOpen(false)}
        title="Технические данные"
        subtitle={`Запись сайта · ${article}`}
        size="xl"
        layer="nested"
      >
        <pre className="max-h-[60vh] overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-800 dark:bg-zinc-800 dark:text-zinc-200">
          {JSON.stringify(card?.record ?? null, null, 2)}
        </pre>
      </Modal>
    </>
  )
}

export default SiteCardModal
